// Lib
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

// Utils
import { getIsLightColor } from '../../../common/colors/coreColorUtil';
import { getBackgroundColor } from '../../../common/elements/utils/elementPropertyUtils';
import { sendAmplitudeEvent } from '../../analytics/amplitudeService';
import { getColorDefaultHexValue } from '../../../common/colors/elementColorFormatUtil';
import { isHexColorFormat } from '../../../common/colors/colorSpaceUtil';

// Actions
import { updateCurrentBoardBackground } from '../../canvas/background/boardBackgroundActions';

// Selectors
import { getCurrentBoard } from '../../element/board/boardSelector';

// Components
import CustomColorInput from '../toolbar/components/selectionTools/colorTools/CustomColorInput';

// Constants
import { DEFAULT_BOARD_BACKGROUND_COLOR } from '../../canvas/background/boardBackgroundConstants';
import { EVENT_TYPE_NAMES } from '../../../common/analytics/amplitudeEventTypesUtil';
import { AMPLITUDE_USER_PROPS, TRACKED_FEATURES } from '../../../common/analytics/statsConstants';

const updateCssBackgroundColor = (color: string): void => {
    document.body.style.setProperty('--ws-canvas-background', color);

    const isLightBackground = getIsLightColor(color);

    if (isLightBackground) {
        document.body.classList.add('custom-bg-light');
        document.body.classList.remove('custom-bg-dark');
    } else {
        document.body.classList.add('custom-bg-dark');
        document.body.classList.remove('custom-bg-light');
    }
};

const mapDispatchToProps = (dispatch: Function) => ({
    dispatchUpdateBoardBackgroundColor: (color: string) => dispatch(updateCurrentBoardBackground({ color })),
});

const mapStateToProps = createStructuredSelector({
    currentBoardColorName: (state: any): string => {
        const board = getCurrentBoard(state);
        return getBackgroundColor(board);
    },
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

type BackgroundColorPickerProps = ReduxProps & {
    openerEventTarget: EventTarget;
};

const BackgroundColorPicker = ({
    openerEventTarget,
    currentBoardColorName,
    dispatchUpdateBoardBackgroundColor,
}: BackgroundColorPickerProps): ReactElement => {
    const [colorHex, setColorHex] = useState('');

    useEffect(() => {
        // if we're already using a custom color, use it as-is
        if (isHexColorFormat(currentBoardColorName)) {
            setColorHex(currentBoardColorName);
            return;
        }

        // if we're using a named color, wait a frame before querying the CSS, otherwise we might get its previous value
        requestAnimationFrame(() => {
            const computedCssColor = getComputedStyle(document.body).getPropertyValue('--ws-canvas-background');
            if (computedCssColor) {
                setColorHex(computedCssColor);
                return;
            }

            // if CSS calculation fails for some reason, use the default hex value for that color name - this will be
            // slightly off as it'll be the fully saturated variant rather than the canvas variant.
            setColorHex(getColorDefaultHexValue(currentBoardColorName) || DEFAULT_BOARD_BACKGROUND_COLOR);
        });
    }, [currentBoardColorName]);

    const setColor = useCallback(
        (color: string): void => {
            sendAmplitudeEvent({
                eventType: EVENT_TYPE_NAMES.SET_BOARD_BACKGROUND,
                userProperties: {
                    [AMPLITUDE_USER_PROPS.FEATURE]: {
                        [TRACKED_FEATURES.BOARD_BACKGROUND]: true,
                    },
                },
            });

            dispatchUpdateBoardBackgroundColor(color);
        },
        [dispatchUpdateBoardBackgroundColor],
    );

    return (
        <CustomColorInput
            openerEventTarget={openerEventTarget}
            setTempColor={updateCssBackgroundColor}
            setColor={setColor}
            initialColor={colorHex}
        />
    );
};

export default connector(BackgroundColorPicker);
